import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardList, Search, User, Calendar, MapPin, FlaskConical,
  Package, FileText, X, Check, ChevronLeft, ChevronRight,
  AlertCircle, HelpCircle, Wallet, Phone, Hash, Building2, Loader2
} from 'lucide-react';
import { customersAPI, labTestsAPI, packagesAPI, bookingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import toast from 'react-hot-toast';
import GregorianDatePicker from '../../components/GregorianDatePicker';
import TimePicker from '../../components/TimePicker';

const NewBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedBranch, branches, canSwitchBranch } = useBranch();

  const userBranchId = user?.branch_id?._id || user?.branch_id || null;
  const isOwner = user?.role === 'owner';

  const testListRef = useRef(null);
  const packageListRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);

  // Customer - Check if customer was passed from NewCustomer page
  const preSelectedCustomer = location.state?.customer || null;
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(preSelectedCustomer);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Booking details - split into date and time for better UX
  const getInitialTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  const [bookingDatePart, setBookingDatePart] = useState(new Date().toISOString().split('T')[0]);
  const [bookingTimePart, setBookingTimePart] = useState(getInitialTime());
  const bookingDate = `${bookingDatePart}T${bookingTimePart}`;
  const [visitType, setVisitType] = useState('in_lab');
  const [selectedVisitBranchId, setSelectedVisitBranchId] = useState('');
  const [homeVisitAddress, setHomeVisitAddress] = useState('');

  // Tests & Packages
  const [allTests, setAllTests] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [packageSearch, setPackageSearch] = useState('');
  const [showTestList, setShowTestList] = useState(false);
  const [showPackageList, setShowPackageList] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [duplicateTests, setDuplicateTests] = useState([]);

  // Notes
  const [customerNotes, setCustomerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // Questions & Answers
  const [allQuestions, setAllQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({});

  const [saving, setSaving] = useState(false);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (testListRef.current && !testListRef.current.contains(e.target)) {
        setShowTestList(false);
      }
      if (packageListRef.current && !packageListRef.current.contains(e.target)) {
        setShowPackageList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle pre-selected customer from NewCustomer page
  useEffect(() => {
    if (preSelectedCustomer) {
      setCustomerSearch(preSelectedCustomer.full_name || '');
      toast.success(`تم تحديد العميل: ${preSelectedCustomer.full_name}`, {
        icon: '👤',
        duration: 3000
      });
    }
  }, [preSelectedCustomer]);

  useEffect(() => {
    if (visitType !== 'home_visit') return;
    if (homeVisitAddress.trim()) return;
    const customerAddress = selectedCustomer?.address || '';
    if (customerAddress) {
      setHomeVisitAddress(customerAddress);
    }
  }, [visitType, selectedCustomer, homeVisitAddress]);

  // Lazy loading
  const [loadingTests, setLoadingTests] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [testsLoaded, setTestsLoaded] = useState(false);
  const [packagesLoaded, setPackagesLoaded] = useState(false);

  // Pagination state for tests
  const [testsPage, setTestsPage] = useState(1);
  const [hasMoreTests, setHasMoreTests] = useState(true);
  const TESTS_PER_PAGE = 30;

  useEffect(() => {
    if (userBranchId && !isOwner && !canSwitchBranch) {
      setSelectedVisitBranchId(userBranchId);
      return;
    }
    if (selectedBranch?._id && !selectedVisitBranchId) {
      setSelectedVisitBranchId(selectedBranch._id);
    }
  }, [userBranchId, isOwner, canSwitchBranch, selectedBranch, selectedVisitBranchId]);

  const loadTestsIfNeeded = async (page = 1, search = '') => {
    if (loadingTests) return;
    setLoadingTests(true);
    try {
      const params = { limit: TESTS_PER_PAGE, page, active: true };
      if (visitType === 'home_visit') {
        params.accepts_home_visit = true;
      }
      if (search) params.search = search;
      const res = await labTestsAPI.getAll(params);
      const items = res.data?.data?.items || [];
      const total = res.data?.data?.total || 0;

      if (page === 1) {
        setAllTests(items);
      } else {
        setAllTests(prev => [...prev, ...items]);
      }
      setTestsPage(page);
      setHasMoreTests(page * TESTS_PER_PAGE < total);
      setTestsLoaded(true);
    } catch (err) {
      console.error('Error fetching tests:', err);
    }
    setLoadingTests(false);
  };

  const loadPackagesIfNeeded = async () => {
    if (packagesLoaded || loadingPackages) return;
    setLoadingPackages(true);
    try {
      const params = { limit: 100, is_available: true };
      if (visitType === 'home_visit') {
        params.accepts_home_visit = true;
      }
      const res = await packagesAPI.getAll(params);
      setAllPackages(res.data?.data?.items || []);
      setPackagesLoaded(true);
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
    setLoadingPackages(false);
  };

  // Search customers
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomers([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      const response = await customersAPI.getAll({ search: query, limit: 10 });
      const data = response.data?.data?.items || response.data?.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      setCustomers([]);
    }
    setSearchingCustomers(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
        setShowCustomerList(true);
      } else {
        setCustomers([]);
        setShowCustomerList(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  // Calculate total
  const isSaudi = selectedCustomer?.nationality === 'Saudi Arabia' ||
    selectedCustomer?.nationality === 'السعودية' ||
    selectedCustomer?.nationality === 'Saudi';
  const vatRate = isSaudi ? 0 : 15;

  const calculateSubtotal = () => {
    const testsTotal = selectedTests.reduce((sum, t) => sum + (t.price || 0), 0);
    const packagesTotal = selectedPackages.reduce((sum, p) => sum + (p.price || 0), 0);
    return testsTotal + packagesTotal;
  };

  const calculateVat = () => {
    return calculateSubtotal() * (vatRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateVat();
  };

  // Debounced API search for tests
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showTestList) {
        setTestsPage(1);
        setHasMoreTests(true);
        loadTestsIfNeeded(1, testSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [testSearch, showTestList, visitType]);

  useEffect(() => {
    if (visitType === 'home_visit') {
      setSelectedTests((prev) => {
        const next = prev.filter((test) => test.accepts_home_visit === true);
        if (next.length !== prev.length) {
          toast('تم حذف تحاليل غير داعمة للزيارة المنزلية تلقائياً', { icon: '⚠️' });
        }
        return next;
      });
      setSelectedPackages((prev) => {
        const next = prev.filter((pkg) => pkg.accepts_home_visit === true);
        if (next.length !== prev.length) {
          toast('تم حذف باقات غير داعمة للزيارة المنزلية تلقائياً', { icon: '⚠️' });
        }
        return next;
      });
    }
  }, [visitType]);

  useEffect(() => {
    if (visitType !== 'home_visit') return;
    const current = branches.find((b) => b._id === selectedVisitBranchId);
    if (current?.home_visit_enabled) return;
    const firstHomeVisit = branches.find((b) => b.home_visit_enabled);
    setSelectedVisitBranchId(firstHomeVisit?._id || '');
  }, [visitType, branches]);

  useEffect(() => {
    setTestsLoaded(false);
    setPackagesLoaded(false);
    setAllTests([]);
    setAllPackages([]);
  }, [visitType]);

  // Local filter as extra safety (API already filters, but this handles UI-side matching)
  const filteredTests = visitType === 'home_visit'
    ? allTests.filter((t) => t.accepts_home_visit === true)
    : allTests;

  const filteredPackages = allPackages.filter((p) => {
    const textMatch = p.name?.toLowerCase().includes(packageSearch.toLowerCase()) || p.name?.includes(packageSearch);
    if (!textMatch) return false;
    return visitType === 'home_visit' ? p.accepts_home_visit === true : true;
  });

  // Add/Remove items
  const toggleTest = (test) => {
    if (selectedTests.find(t => t._id === test._id)) {
      setSelectedTests(selectedTests.filter(t => t._id !== test._id));
    } else {
      setSelectedTests([...selectedTests, test]);
    }
  };

  const togglePackage = (pkg) => {
    if (selectedPackages.find(p => p._id === pkg._id)) {
      setSelectedPackages(selectedPackages.filter(p => p._id !== pkg._id));
    } else {
      setSelectedPackages([...selectedPackages, pkg]);
    }
  };

  const updateTestPrice = (testId, newPrice) => {
    setSelectedTests(selectedTests.map(t =>
      t._id === testId ? { ...t, price: parseFloat(newPrice) || 0 } : t
    ));
  };

  const getCustomerName = (customer) => {
    if (!customer) return '';
    if (customer.full_name) return customer.full_name;
    const firstName = customer.first_name || customer.firstName || '';
    const lastName = customer.last_name || customer.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'غير محدد';
  };

  // Check for duplicate tests
  useEffect(() => {
    const duplicates = [];
    const packageTestIds = new Set();
    const packageTestDetails = new Map();

    selectedPackages.forEach(pkg => {
      if (pkg.tests && pkg.tests.length > 0) {
        pkg.tests.forEach(test => {
          packageTestIds.add(test._id);
          packageTestDetails.set(test._id, {
            testName: test.name_ar || test.name_en,
            packageName: pkg.name_ar || pkg.name_en || pkg.name
          });
        });
      }
    });

    selectedTests.forEach(test => {
      if (packageTestIds.has(test._id)) {
        const details = packageTestDetails.get(test._id);
        duplicates.push({
          testId: test._id,
          testName: test.name_ar || test.name_en,
          packageName: details.packageName
        });
      }
    });

    setDuplicateTests(duplicates);
  }, [selectedTests, selectedPackages]);

  // Collect questions
  const collectQuestions = useCallback(async () => {
    const questions = [];

    for (const test of selectedTests) {
      if (test.has_questions && test.questions?.length > 0) {
        test.questions.forEach((q, idx) => {
          questions.push({
            ...q,
            testId: test._id,
            testName: test.name_en,
            source: 'test',
            uniqueId: `test-${test._id}-${idx}`
          });
        });
      }
    }

    for (const pkg of selectedPackages) {
      if (pkg.tests && pkg.tests.length > 0) {
        for (const pkgTest of pkg.tests) {
          try {
            const fullTest = allTests.find(t => t._id === pkgTest._id);
            if (fullTest?.has_questions && fullTest.questions?.length > 0) {
              fullTest.questions.forEach((q, idx) => {
                if (!questions.find(existing => existing.question_text === q.question_text)) {
                  questions.push({
                    ...q,
                    testId: fullTest._id,
                    testName: fullTest.name_en,
                    packageName: pkg.name,
                    source: 'package',
                    uniqueId: `pkg-${pkg._id}-${fullTest._id}-${idx}`
                  });
                }
              });
            }
          } catch (e) {
            console.log('Error getting test questions:', e);
          }
        }
      }
    }

    setAllQuestions(questions);
  }, [selectedTests, selectedPackages, allTests]);

  const handleProceedToConfirm = () => {
    if (!selectedCustomer) {
      toast.error('يرجى اختيار العميل');
      return;
    }
    if (selectedTests.length === 0 && selectedPackages.length === 0) {
      toast.error('يرجى اختيار تحليل أو باقة واحدة على الأقل');
      return;
    }
    if (!selectedVisitBranchId) {
      toast.error('يرجى اختيار الفرع');
      return;
    }
    if (visitType === 'home_visit' && !homeVisitAddress.trim()) {
      toast.error('يرجى إدخال عنوان الزيارة المنزلية');
      return;
    }
    if (duplicateTests.length > 0) {
      toast.error('يوجد تحاليل مكررة بين التحاليل الفردية والباقات. يرجى حذف التحليل الفردي أو الباقة');
      return;
    }
    collectQuestions();
    setCurrentStep(2);
  };

  const handleSubmit = async () => {
    if (saving) return;

    const effectiveBranchId = (userBranchId && !isOwner && !canSwitchBranch)
      ? userBranchId
      : (selectedVisitBranchId || selectedBranch?._id || null);

    if (!selectedCustomer?._id) {
      toast.error('يرجى اختيار العميل');
      return;
    }
    if (!effectiveBranchId) {
      toast.error('يرجى اختيار الفرع');
      return;
    }
    if (selectedTests.length === 0 && selectedPackages.length === 0) {
      toast.error('يرجى اختيار تحليل أو باقة واحدة على الأقل');
      return;
    }
    if (visitType === 'home_visit') {
      if (!homeVisitAddress?.trim()) {
        toast.error('يرجى إدخال عنوان الزيارة المنزلية');
        return;
      }
      const branch = branches.find((b) => b._id === effectiveBranchId);
      if (!branch?.home_visit_enabled) {
        toast.error('الفرع المحدد لا يدعم الزيارات المنزلية');
        return;
      }
      const schedule = Array.isArray(branch.home_visit_schedule) ? branch.home_visit_schedule : [];
      if (schedule.length === 0) {
        toast.error('لا يوجد جدول زيارات منزلية مفعّل لهذا الفرع');
        return;
      }
      const selectedDate = new Date(bookingDate);
      if (Number.isNaN(selectedDate.getTime())) {
        toast.error('تاريخ/وقت الحجز غير صالح');
        return;
      }
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const selectedDay = dayNames[selectedDate.getDay()];
      const selectedMinutes = selectedDate.getHours() * 60 + selectedDate.getMinutes();
      const toMinutes = (hhmm) => {
        const [h, m] = String(hhmm || '').split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
      };
      const inSchedule = schedule.some((slot) => {
        if (String(slot.day_of_week || '').toLowerCase() !== selectedDay) return false;
        const start = toMinutes(slot.start_time);
        const end = toMinutes(slot.end_time);
        if (start === null || end === null) return false;
        return selectedMinutes >= start && selectedMinutes <= end;
      });
      if (!inSchedule) {
        toast.error('وقت الحجز خارج جدول الزيارات المنزلية للفرع');
        return;
      }
    }

    setSaving(true);
    try {
      const bookingData = {
        customer_id: selectedCustomer._id,
        booking_date: new Date(bookingDate).toISOString(),
        visit_type: visitType,
        branch_id: effectiveBranchId,
        home_visit_address: visitType === 'home_visit' ? homeVisitAddress.trim() : null,
        tests: selectedTests.map(t => ({ lab_test_id: t._id, price: t.price || 0 })),
        packages: selectedPackages.map(p => ({ package_id: p._id, price: p.price || 0 })),
        customer_notes: customerNotes || null,
        internal_notes: internalNotes || null,
        question_answers: questionAnswers
      };

      const response = await bookingsAPI.create(bookingData);
      const bookingId = response.data?.data?._id;
      toast.success('تم إنشاء الحجز بنجاح');
      navigate(`/bookings/${bookingId}`);
    } catch (err) {
      const details = err?.response?.data?.errors;
      const firstDetail = Array.isArray(details) && details.length > 0 ? details[0]?.message : null;
      toast.error(firstDetail || err.response?.data?.message || 'حدث خطأ أثناء إنشاء الحجز');
      console.error('Create booking failed:', err?.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  const total = calculateTotal();
  const homeVisitBranches = branches.filter((b) => b.home_visit_enabled === true);
  const selectableBranches = visitType === 'home_visit' ? homeVisitBranches : branches;
  const selectedVisitBranch = branches.find((b) => b._id === selectedVisitBranchId) || null;

  const formatHomeVisitSchedule = (branch) => {
    if (!branch?.home_visit_schedule?.length) return 'لا يوجد جدول محدد';
    const dayMap = {
      saturday: 'السبت',
      sunday: 'الأحد',
      monday: 'الاثنين',
      tuesday: 'الثلاثاء',
      wednesday: 'الأربعاء',
      thursday: 'الخميس',
      friday: 'الجمعة'
    };
    const uniqueDays = [...new Set(branch.home_visit_schedule.map((s) => dayMap[s.day_of_week] || s.day_of_week))];
    const slot = branch.home_visit_schedule[0];
    return `${uniqueDays.join('، ')} | ${slot.start_time} - ${slot.end_time}`;
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Step 1: Booking Form
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Customer & Date Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Section */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">العميل</h2>
              <p className="text-xs text-slate-500">اختر العميل للحجز</p>
            </div>
          </div>

          {selectedCustomer ? (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-start justify-between">
                <div className="space-y-2.5 flex-1">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-slate-600">
                        {getCustomerName(selectedCustomer)?.charAt(0) || '؟'}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{getCustomerName(selectedCustomer)}</p>
                      <p className="text-xs text-slate-500">{selectedCustomer.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 pt-1 border-t border-slate-200/60">
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {selectedCustomer.national_id || '-'}
                    </span>
                    <span>{selectedCustomer?.gender === 'Male' ? 'ذكر' : 'أنثى'}</span>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}
                  className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="بحث بالاسم أو الهوية أو رقم الجوال..."
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>

              {showCustomerList && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 max-h-72 overflow-y-auto z-20">
                  <div className="sticky top-0 bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex justify-between items-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">نتائج البحث</span>
                    <button onClick={() => setShowCustomerList(false)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </div>
                  {searchingCustomers ? (
                    <div className="p-6 flex flex-col items-center justify-center">
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin mb-2" />
                      <span className="text-sm text-slate-500">جاري البحث...</span>
                    </div>
                  ) : customers.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">
                      {customerSearch.length < 2 ? 'اكتب حرفين على الأقل للبحث' : 'لا توجد نتائج'}
                    </div>
                  ) : (
                    customers.map(c => (
                      <button
                        key={c._id}
                        onClick={() => {
                          setSelectedCustomer(c);
                          setShowCustomerList(false);
                          setCustomerSearch('');
                        }}
                        className="w-full text-right px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-bold text-slate-600">
                              {getCustomerName(c)?.charAt(0) || '؟'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">{getCustomerName(c)}</p>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {c.phone || '-'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Hash className="w-3 h-3" />
                                {c.national_id || '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Date & Visit Type */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-slate-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">تاريخ ونوع الزيارة</h2>
              <p className="text-xs text-slate-500">حدد موعد الحجز</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 items-end">
              <GregorianDatePicker
                label={<span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التاريخ</span>}
                value={bookingDatePart}
                onChange={setBookingDatePart}
              />
              <TimePicker
                label={<span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الوقت</span>}
                value={bookingTimePart}
                onChange={setBookingTimePart}
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">نوع الزيارة</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setVisitType('in_lab')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${visitType === 'in_lab'
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Building2 className="w-4 h-4" strokeWidth={2} />
                    في المختبر
                  </span>
                </button>
                <button
                  onClick={() => setVisitType('home_visit')}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${visitType === 'home_visit'
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <MapPin className="w-4 h-4" strokeWidth={2} />
                    زيارة منزلية
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                الفرع
              </label>
              <select
                value={selectedVisitBranchId}
                onChange={(e) => setSelectedVisitBranchId(e.target.value)}
                disabled={userBranchId && !isOwner && !canSwitchBranch}
                className="w-full px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <option value="">اختر الفرع</option>
                {selectableBranches.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.branch_name}
                  </option>
                ))}
              </select>
              {visitType === 'home_visit' && selectedVisitBranch && (
                <p className="text-xs text-blue-700 mt-2">
                  أوقات الزيارات: {formatHomeVisitSchedule(selectedVisitBranch)}
                </p>
              )}
              {visitType === 'home_visit' && selectableBranches.length === 0 && (
                <p className="text-xs text-rose-600 mt-2">لا توجد فروع مفعلة للزيارات المنزلية حالياً.</p>
              )}
            </div>

            {visitType === 'home_visit' && (
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  عنوان الزيارة المنزلية
                </label>
                <textarea
                  value={homeVisitAddress}
                  onChange={(e) => setHomeVisitAddress(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                  rows={2}
                  placeholder="مثال: الرياض، حي اليرموك، شارع..."
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tests & Packages */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-slate-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">التحاليل والباقات</h2>
            <p className="text-xs text-slate-500">اختر التحاليل أو الباقات المطلوبة</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tests */}
          <div ref={testListRef}>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">التحاليل الفردية</label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  value={testSearch}
                  onChange={(e) => { setTestSearch(e.target.value); setShowTestList(true); }}
                  onFocus={() => { if (!testsLoaded) loadTestsIfNeeded(1, ''); setShowTestList(true); }}
                  placeholder={loadingTests ? 'جاري التحميل...' : 'ابحث عن تحليل...'}
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>

              {showTestList && (
                <div
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 max-h-60 overflow-y-auto z-20"
                  onScroll={(e) => {
                    const el = e.target;
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                      if (hasMoreTests && !loadingTests) {
                        loadTestsIfNeeded(testsPage + 1, testSearch);
                      }
                    }
                  }}
                >
                  <div className="sticky top-0 bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex justify-between items-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">اختر التحاليل</span>
                    <button onClick={() => setShowTestList(false)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </div>
                  {!testsLoaded && loadingTests ? (
                    <div className="p-6 flex flex-col items-center justify-center">
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin mb-2" />
                      <span className="text-sm text-slate-500">جاري التحميل...</span>
                    </div>
                  ) : filteredTests.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">لا توجد تحاليل</div>
                  ) : (
                    <>
                      {filteredTests.map(test => {
                        const isSelected = selectedTests.find(t => t._id === test._id);
                        return (
                          <button
                            key={test._id}
                            onClick={() => toggleTest(test)}
                            className={`w-full text-right px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 flex items-center justify-between transition-colors ${isSelected ? 'bg-emerald-50' : ''
                              }`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {isSelected && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={2.5} />}
                              <div className="min-w-0">
                                <span className="font-medium text-slate-900 text-sm block truncate">{test.name_en}</span>
                                {test.aliases && <span className="text-xs text-slate-400 block truncate">{test.aliases}</span>}
                              </div>
                              {test.has_questions && <HelpCircle className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={2} />}
                            </div>
                            <span className="text-sm font-semibold text-slate-700 tabular-nums flex-shrink-0 mr-2">{test.price || 0} ر.س</span>
                          </button>
                        );
                      })}
                      {loadingTests && (
                        <div className="p-3 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                          <span className="text-xs text-slate-500 mr-2">جاري تحميل المزيد...</span>
                        </div>
                      )}
                      {!hasMoreTests && filteredTests.length > 0 && (
                        <div className="p-2 text-center text-xs text-slate-400">تم عرض جميع التحاليل</div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Packages */}
          <div ref={packageListRef}>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 block">الباقات</label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
                <input
                  type="text"
                  value={packageSearch}
                  onChange={(e) => { setPackageSearch(e.target.value); setShowPackageList(true); }}
                  onFocus={() => { loadPackagesIfNeeded(); setShowPackageList(true); }}
                  placeholder={loadingPackages ? 'جاري التحميل...' : 'ابحث عن باقة...'}
                  className="w-full pr-10 pl-4 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                />
              </div>

              {showPackageList && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 max-h-60 overflow-y-auto z-20">
                  <div className="sticky top-0 bg-slate-50 px-4 py-2.5 border-b border-slate-100 flex justify-between items-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">اختر الباقات</span>
                    <button onClick={() => setShowPackageList(false)} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </div>
                  {loadingPackages ? (
                    <div className="p-6 flex flex-col items-center justify-center">
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin mb-2" />
                      <span className="text-sm text-slate-500">جاري التحميل...</span>
                    </div>
                  ) : filteredPackages.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">لا توجد باقات</div>
                  ) : (
                    filteredPackages.slice(0, 20).map(pkg => {
                      const isSelected = selectedPackages.find(p => p._id === pkg._id);
                      return (
                        <button
                          key={pkg._id}
                          onClick={() => togglePackage(pkg)}
                          className={`w-full text-right px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 flex items-center justify-between transition-colors ${isSelected ? 'bg-violet-50' : ''
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            {isSelected && <Check className="w-4 h-4 text-violet-600" strokeWidth={2.5} />}
                            <Package className="w-4 h-4 text-slate-400" strokeWidth={2} />
                            <span className="font-medium text-slate-900 text-sm">{pkg.name}</span>
                          </div>
                          <span className="text-sm font-semibold text-slate-700 tabular-nums">{pkg.price || 0} ر.س</span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Selected Items */}
        {(selectedTests.length > 0 || selectedPackages.length > 0) && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3 block">العناصر المختارة ({selectedTests.length + selectedPackages.length})</label>
            <div className="flex flex-wrap gap-2">
              {selectedTests.map(test => (
                <div key={test._id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200/60">
                  <button onClick={() => toggleTest(test)} className="text-slate-400 hover:text-rose-500 transition-colors">
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                  <span className="text-sm font-medium text-slate-700">{test.name_en}</span>
                  <input
                    type="number"
                    value={test.price || 0}
                    onChange={(e) => updateTestPrice(test._id, e.target.value)}
                    className="w-16 px-2 py-1 bg-white border border-slate-200 rounded-md text-sm text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                    min="0"
                  />
                  <span className="text-xs text-slate-500">ر.س</span>
                </div>
              ))}
              {selectedPackages.map(pkg => (
                <div key={pkg._id} className="flex items-center gap-2 bg-violet-50 rounded-lg px-3 py-2 border border-violet-200/60">
                  <button onClick={() => togglePackage(pkg)} className="text-violet-400 hover:text-rose-500 transition-colors">
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                  <Package className="w-3.5 h-3.5 text-violet-500" strokeWidth={2} />
                  <span className="text-sm font-medium text-slate-700">{pkg.name}</span>
                  <span className="text-sm font-semibold text-violet-600 tabular-nums">{pkg.price} ر.س</span>
                </div>
              ))}
            </div>

            {/* Duplicate Tests Warning */}
            {duplicateTests.length > 0 && (
              <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div className="flex-1">
                    <h4 className="text-rose-800 font-semibold text-sm mb-2">تحذير: تحاليل مكررة!</h4>
                    <p className="text-rose-700 text-xs mb-3">
                      التحاليل التالية موجودة كتحاليل فردية وأيضاً ضمن الباقات:
                    </p>
                    <div className="space-y-1.5">
                      {duplicateTests.map((dup, idx) => (
                        <div key={idx} className="text-xs text-rose-700 bg-rose-100 rounded-lg px-3 py-2">
                          <span className="font-medium">{dup.testName}</span>
                          <span className="text-rose-600"> ← موجود في: </span>
                          <span className="font-medium">{dup.packageName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Amount Summary */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-slate-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">ملخص المبلغ</h2>
            <p className="text-xs text-slate-500">سيتم تأكيد الدفع من صفحة التفاصيل</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">المجموع الفرعي</span>
            <span className="font-semibold text-slate-900 tabular-nums">{calculateSubtotal().toFixed(2)} ر.س</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">ضريبة القيمة المضافة ({vatRate}%)</span>
            <span className="font-semibold text-slate-900 tabular-nums">{calculateVat().toFixed(2)} ر.س</span>
          </div>
          {vatRate === 0 && (
            <p className="text-xs text-emerald-600 flex items-center gap-1">
              <Check className="w-3 h-3" />
              معفى من الضريبة (جنسية سعودية)
            </p>
          )}
          <div className="flex justify-between pt-3 border-t border-slate-200">
            <span className="font-bold text-slate-900">المبلغ المطلوب</span>
            <span className="font-bold text-slate-900 text-lg tabular-nums">{total.toFixed(2)} ر.س</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-slate-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">ملاحظات</h2>
            <p className="text-xs text-slate-500">أضف ملاحظات للحجز (اختياري)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">ملاحظات للعميل</label>
            <textarea
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
              rows={3}
              placeholder="ادخل ملاحظات لإرسالها للعميل"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">ملاحظات داخلية</label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
              rows={3}
              placeholder="للاستعمال الداخلي فقط"
            />
          </div>
        </div>
      </div>

      {/* Proceed Button */}
      <div className="flex justify-center pt-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleProceedToConfirm}
          className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <span>متابعة للمراجعة</span>
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </motion.button>
      </div>
    </div>
  );

  // Step 2: Confirmation with Questions
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Customer & Booking Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] overflow-hidden">
          <div className="bg-[#1E293B] px-6 py-4">
            <h2 className="font-semibold text-slate-100 flex items-center gap-2">
              <User className="w-5 h-5" strokeWidth={2} />
              معلومات العميل
            </h2>
          </div>
          <div className="p-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الاسم الكامل</p>
              <p className="font-semibold text-slate-900 mt-1">{getCustomerName(selectedCustomer)}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الجوال</p>
              <p className="font-semibold text-slate-900 mt-1">{selectedCustomer?.phone || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الهوية</p>
              <p className="font-semibold text-slate-900 mt-1">{selectedCustomer?.national_id || '-'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الجنس</p>
              <p className="font-semibold text-slate-900 mt-1">{selectedCustomer?.gender === 'Male' ? 'ذكر' : 'أنثى'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">تاريخ الميلاد</p>
              <p className="font-semibold text-slate-900 mt-1">
                {selectedCustomer?.date_of_birth ? new Date(selectedCustomer.date_of_birth).toLocaleDateString('en-GB') : '-'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">العمر</p>
              <p className="font-semibold text-slate-900 mt-1">{calculateAge(selectedCustomer?.date_of_birth)} سنة</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] overflow-hidden">
          <div className="bg-[#1E293B] px-6 py-4">
            <h2 className="font-semibold text-slate-100 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" strokeWidth={2} />
              ملخص الحجز
            </h2>
          </div>
          <div className="p-6 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">تاريخ الحجز</p>
              <p className="font-semibold text-slate-900 mt-1">{new Date(bookingDate).toLocaleString('en-US')}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">نوع الزيارة</p>
              <p className="font-semibold text-slate-900 mt-1">{visitType === 'in_lab' ? 'في المختبر' : 'زيارة منزلية'}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الفرع</p>
              <p className="font-semibold text-slate-900 mt-1 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-400" strokeWidth={2} />
                {selectedVisitBranch?.branch_name || 'غير محدد'}
              </p>
            </div>
            {visitType === 'home_visit' && (
              <div className="col-span-2">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">عنوان الزيارة</p>
                <p className="font-semibold text-slate-900 mt-1">{homeVisitAddress || '-'}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المبلغ (شامل {vatRate}% ضريبة)</p>
              <p className="font-bold text-slate-900 text-xl mt-1 tabular-nums">{total.toFixed(2)} ر.س</p>
              {vatRate === 0 && <p className="text-xs text-emerald-600">معفى من الضريبة</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Selected Tests & Packages */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
        <h2 className="font-semibold text-slate-900 mb-4">التحاليل والباقات المختارة</h2>
        <div className="space-y-2">
          {selectedTests.map(test => (
            <div key={test._id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
              <div className="flex items-center gap-3">
                <FlaskConical className="w-4 h-4 text-slate-500" strokeWidth={2} />
                <span className="font-medium text-slate-900 text-sm">{test.name_en}</span>
                {test.has_questions && (
                  <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">يحتوي أسئلة</span>
                )}
              </div>
              <span className="font-bold text-slate-900 text-sm tabular-nums">{test.price} ر.س</span>
            </div>
          ))}
          {selectedPackages.map(pkg => (
            <div key={pkg._id} className="flex items-center justify-between bg-violet-50 rounded-xl px-4 py-3 border border-violet-100">
              <div className="flex items-center gap-3">
                <Package className="w-4 h-4 text-violet-500" strokeWidth={2} />
                <span className="font-medium text-slate-900 text-sm">{pkg.name}</span>
              </div>
              <span className="font-bold text-slate-900 text-sm tabular-nums">{pkg.price} ر.س</span>
            </div>
          ))}
        </div>
      </div>

      {/* Questions Section */}
      {allQuestions.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-amber-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">أسئلة التحاليل</h2>
              <p className="text-xs text-slate-500">يرجى الإجابة على الأسئلة التالية</p>
            </div>
          </div>

          <div className="space-y-4">
            {allQuestions.map((q, idx) => (
              <div key={q.uniqueId} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-6 h-6 rounded-full bg-[#1E293B] text-slate-100 text-xs flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 text-sm">{q.question_text}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {q.source === 'package' ? `من باقة: ${q.packageName} - ` : ''}
                      التحليل: {q.testName}
                      {q.is_required && <span className="text-rose-500 mr-2">*</span>}
                    </p>
                  </div>
                </div>

                {q.question_type === 'yes_no' && (
                  <div className="flex gap-2 mr-9">
                    <button
                      onClick={() => setQuestionAnswers({ ...questionAnswers, [q.uniqueId]: 'نعم' })}
                      className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${questionAnswers[q.uniqueId] === 'نعم'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                        }`}
                    >
                      نعم
                    </button>
                    <button
                      onClick={() => setQuestionAnswers({ ...questionAnswers, [q.uniqueId]: 'لا' })}
                      className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${questionAnswers[q.uniqueId] === 'لا'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                        }`}
                    >
                      لا
                    </button>
                  </div>
                )}

                {q.question_type === 'text' && (
                  <input
                    type="text"
                    value={questionAnswers[q.uniqueId] || ''}
                    onChange={(e) => setQuestionAnswers({ ...questionAnswers, [q.uniqueId]: e.target.value })}
                    className="w-full mr-9 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="أدخل إجابتك"
                  />
                )}

                {q.question_type === 'number' && (
                  <input
                    type="number"
                    value={questionAnswers[q.uniqueId] || ''}
                    onChange={(e) => setQuestionAnswers({ ...questionAnswers, [q.uniqueId]: e.target.value })}
                    className="w-full mr-9 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder="أدخل الرقم"
                  />
                )}

                {(q.question_type === 'single_select' || q.question_type === 'multi_select') && (
                  <div className="flex flex-wrap gap-2 mr-9">
                    {(q.options || []).map((opt, optIdx) => (
                      <button
                        key={optIdx}
                        onClick={() => setQuestionAnswers({ ...questionAnswers, [q.uniqueId]: opt })}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${questionAnswers[q.uniqueId] === opt
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-[#1E293B]/20 text-[#1E293B] hover:bg-slate-50'
                          }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-3 pt-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setCurrentStep(1)}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-xl font-medium text-sm hover:bg-slate-50 transition-all duration-200"
        >
          <ChevronRight className="w-4 h-4 text-blue-600" strokeWidth={2} />
          <span>رجوع</span>
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>جاري الحفظ...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" strokeWidth={2.5} />
              <span>تأكيد الحجز</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">حجز جديد</h1>
                <p className="text-slate-500 text-sm">إنشاء حجز تحاليل جديد</p>
              </div>
            </div>
          </div>

          {/* Steps Indicator */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_0_rgb(0_0_0_/_0.02)] p-4">
            <div className="flex items-center justify-center gap-4">
              <div className={`flex items-center gap-2 ${currentStep === 1 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                  1
                </div>
                <span className="text-sm font-medium text-[#1E293B] hidden sm:inline">بيانات الحجز</span>
              </div>
              <div className="w-12 h-0.5 bg-slate-200 rounded-full">
                <div className={`h-full bg-blue-600 rounded-full transition-all duration-300 ${currentStep === 2 ? 'w-full' : 'w-0'}`} />
              </div>
              <div className={`flex items-center gap-2 ${currentStep === 2 ? 'opacity-100' : 'opacity-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${currentStep === 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                  2
                </div>
                <span className="text-sm font-medium text-[#1E293B] hidden sm:inline">المراجعة والتأكيد</span>
              </div>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: currentStep === 1 ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: currentStep === 1 ? 20 : -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentStep === 1 ? renderStep1() : renderStep2()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default NewBooking;
